import { App } from '@capacitor/app';
import {
  AppUpdateAvailability,
  CapacitorUpdater,
} from '@capgo/capacitor-updater';
import { atom } from 'jotai';

import { logger } from '@/features/logging/service/logger';
import { getPlatform } from '@/shared/utils/platform';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

export interface AvailableUpdate {
  version: string;
  flexibleUpdateAllowed: boolean;
}

export const availableUpdateAtom = atom<AvailableUpdate | undefined>(undefined);
availableUpdateAtom.onMount = setLastUpdateInfo => {
  if (getPlatform() === 'WEB') return;

  let interval: ReturnType<typeof setInterval> | null = null;

  const startInterval = () => {
    interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);
  };

  const stopInterval = () => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  const checkForUpdate = async () => {
    const {
      availableVersionName: newVersion,
      flexibleUpdateAllowed = false,
      updateAvailability,
    } = await CapacitorUpdater.getAppUpdateInfo();

    setLastUpdateInfo(prev => {
      if (updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        return undefined;
      }
      if (prev && prev.version === newVersion) {
        return prev;
      }
      if (!newVersion) {
        // This should not happen, but just in case
        logger.warn('Received update info with no version');
        return undefined;
      }
      return {
        version: newVersion,
        flexibleUpdateAllowed,
      };
    });
  };

  void checkForUpdate();
  startInterval();

  const resumeListener = App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      void checkForUpdate();
      startInterval();
    } else {
      stopInterval();
    }
  });

  return () => {
    void resumeListener.then(listener => listener.remove());
    stopInterval();
  };
};
